(function (window, document) {
    'use strict';

    function debounce(callback, wait) {
        let timer;

        return function () {
            const args = arguments;
            clearTimeout(timer);
            timer = window.setTimeout(function () {
                callback(...args);
            }, wait);
        };
    }

    function buildEndpoint(endpoint, query, langId) {
        const separator = endpoint.includes('?') ? '&' : '?';
        let url = endpoint + separator + 's=' + encodeURIComponent(query);

        if (langId) {
            url += '&lang_id=' + encodeURIComponent(langId);
        }

        return url;
    }

    function setStatus(status, state, message) {
        if (!status) {
            return;
        }

        status.classList.remove('is-visible', 'is-loading', 'is-error', 'is-info');
        status.innerHTML = '';

        if (!message) {
            return;
        }

        status.classList.add('is-visible');

        if (state === 'loading') {
            status.classList.add('is-loading');
            status.innerHTML = '<span class="jp-typeahead-spinner" aria-hidden="true"></span><span>' + message + '</span>';
            return;
        }

        if (state === 'error') {
            status.classList.add('is-error');
        } else {
            status.classList.add('is-info');
        }

        status.textContent = message;
    }

    function updateMetric(element, value) {
        if (!element) {
            return;
        }

        element.textContent = value;
    }

    function closePanel(panel) {
        if (!panel) {
            return;
        }

        panel.classList.remove('is-open');
        panel.innerHTML = '';
    }

    function clearSelection(form, fieldMap) {
        Object.keys(fieldMap).forEach(function (key) {
            if (!fieldMap[key]) {
                return;
            }

            const field = form ? form.querySelector('[name="' + fieldMap[key] + '"]') : null;
            if (field) {
                field.value = '';
            }
        });
    }

    function setSelection(form, input, item, fieldMap) {
        let field;

        input.value = item.name || '';

        Object.keys(fieldMap).forEach(function (key) {
            if (!fieldMap[key]) {
                return;
            }

            field = form ? form.querySelector('[name="' + fieldMap[key] + '"]') : null;

            if (!field) {
                return;
            }

            if (key === 'name') {
                field.value = item.name || '';
                return;
            }

            if (key === 'lat') {
                field.value = item.latitude || '';
                return;
            }

            if (key === 'lng') {
                field.value = item.longitude || '';
            }
        });
    }

    function init(config) {
        let input = config?.input ?? null;
        let wrapper = config?.wrapper ?? null;
        let panel = config?.panel ?? null;
        let status = config?.status ?? null;
        const metrics = config?.metrics ?? {};
        const endpoint = config?.endpoint ?? '';
        const form = input ? input.closest('form') : null;
        const fieldMap = config?.fieldMap ?? {};
        const minLength = config?.minLength ?? 2;
        const wait = config?.debounce ?? 125;
        const buttonClassName = config?.suggestionClassName ?? 'jp-suggestion';
        const panelClassName = config?.panelClassName ?? 'jp-suggestions';
        const statusClassName = config?.statusClassName ?? 'jp-typeahead-status';
        let requestSequence = 0;
        let activeRequest = null;
        let lastRequestedQuery = '';
        let lastSelectedName = '';

        if (!input || !endpoint) {
            return null;
        }

        if (!wrapper) {
            wrapper = input.parentNode;
        }

        if (!panel) {
            panel = document.createElement('div');
            wrapper.appendChild(panel);
        }

        panel.className = panelClassName;
        panel.setAttribute('role', 'listbox');

        if (!status) {
            status = document.createElement('div');
            wrapper.appendChild(status);
        }

        status.className = statusClassName;
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');

        function updateMetrics(query, duration, count, error) {
            updateMetric(metrics.query, query || '-');
            updateMetric(metrics.duration, duration ? String(duration) + ' ms' : '-');
            updateMetric(metrics.count, typeof count === 'number' ? String(count) : '-');
            updateMetric(metrics.error, error || '-');
        }

        function buildSuggestions(data, query, duration) {
            panel.innerHTML = '';

            if (!Array.isArray(data) || data.length === 0) {
                closePanel(panel);
                setStatus(status, 'info', config?.noResultsText || 'No locations found.');
                updateMetrics(query, duration, 0, '');
                return;
            }

            data.slice(0, 12).forEach(function (item) {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = buttonClassName;
                button.textContent = item.name || '';
                button.addEventListener('click', function () {
                    setSelection(form, input, item, fieldMap);
                    lastSelectedName = item.name || '';
                    setStatus(status, '', '');
                    closePanel(panel);
                });
                panel.appendChild(button);
            });

            panel.classList.add('is-open');
            setStatus(status, '', '');
            updateMetrics(query, duration, data.length, '');
        }

        input.addEventListener('input', debounce(function () {
            const query = input.value.trim();
            let currentRequest = null;
            let requestId;
            const startedAt = window.performance?.now?.() ?? Date.now();

            if (query !== lastSelectedName) {
                clearSelection(form, fieldMap);
            }

            if (activeRequest) {
                activeRequest.abort();
                activeRequest = null;
            }

            if (query.length < minLength) {
                lastRequestedQuery = '';
                closePanel(panel);
                setStatus(status, '', '');
                updateMetrics(query, null, null, '');
                return;
            }

            if (query === lastRequestedQuery) {
                return;
            }

            lastRequestedQuery = query;
            requestSequence += 1;
            requestId = requestSequence;
            currentRequest = window.AbortController ? new window.AbortController() : null;
            activeRequest = currentRequest;

            setStatus(status, 'loading', config?.loadingText || 'Loading suggestions...');
            updateMetrics(query, null, null, '');

            window.fetch(
                buildEndpoint(
                    endpoint,
                    query,
                    config?.getLangId?.() ?? config?.langId
                ),
                currentRequest ? { signal: currentRequest.signal, credentials: 'same-origin' } : { credentials: 'same-origin' }
                )
                .then(function (response) {
                    if (!response.ok) {
                        const error = new Error('Lookup failed.');
                        error.status = response.status;
                        throw error;
                    }

                    return response.json();
                })
                .then(function (data) {
                    const duration = Math.round((window.performance?.now?.() ?? Date.now()) - startedAt);

                    if (requestId !== requestSequence || input.value.trim() !== query) {
                        return;
                    }

                    buildSuggestions(data, query, duration);
                })
                .catch(function (error) {
                    const duration = Math.round((window.performance?.now?.() ?? Date.now()) - startedAt);
                    let message;

                    if (error?.name === 'AbortError') {
                        return;
                    }

                    closePanel(panel);
                    message = error?.status === 429
                        ? (config?.rateLimitedText || 'Too many searches. Please pause and try again.')
                        : (config?.errorText || 'Suggestions are temporarily unavailable. Please try again.');
                    setStatus(status, 'error', message);
                    updateMetrics(query, duration, 0, message);
                })
                .finally(function () {
                    if (activeRequest === currentRequest) {
                        activeRequest = null;
                    }
                });
        }, wait));

        input.addEventListener('focus', function () {
            config?.onFocus?.(panel);
        });

        input.addEventListener('keydown', function (event) {
            if (event.key === 'Escape') {
                closePanel(panel);
                setStatus(status, '', '');
            }
        });

        document.addEventListener('click', function (event) {
            if (!wrapper.contains(event.target)) {
                closePanel(panel);
            }
        });

        return {
            close: function () {
                closePanel(panel);
                setStatus(status, '', '');
            }
        };
    }

    window.SpindogsLocalityTypeahead = {
        init: init
    };
})(window, document);
